import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Product } from 'src/app/model/product';
import { AppModeService } from '../../../services/app-mode.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private apiUrl: string = '/api/';
  private mobileApiUrl: string = '/api/mobile/';
  private mainUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private appModeService: AppModeService,
  ) {}

  /**
   * Get the appropriate API endpoint based on app/desktop mode
   */
  private getApiUrl(): string {
    return this.appModeService.isApp() ? this.mobileApiUrl : this.apiUrl;
  }

  mapToCardProducts(
    products: Product[],
    selectedColors: string[] = [],
  ): Array<{
    id: string;
    imagePath: string;
    name: string;
    rating: number;
    price: string;
    isNew: boolean;
    isFeatured: boolean;
  }> {
    return products.map((product: Product) => {
      let media;

      if (selectedColors.length > 0) {
        const colorMatch = product.productColors.find((c) =>
          selectedColors.includes(c.color),
        );
        media = colorMatch?.productMedia?.[0];
      }

      if (!media) {
        media = product.productColors?.[0]?.productMedia?.[0];
      }

      const imagePath = environment.apiUrl + (media?.mediaUrl || '');
      const isFeatured =
        product.productColors?.some((c) => c.isFeatured) ?? false;

      return {
        id: product.id,
        imagePath,
        name: product.name,
        rating: 3,
        price: product.price,
        isNew: false,
        isFeatured,
      };
    });
  }

  /**
   * Get all products
   * Calls /api/products for web or /api/mobile/products for app
   */
  getProducts(): Observable<Product[]> {
    const apiUrl = this.getApiUrl();
    return this.http.get<Product[]>(this.mainUrl + apiUrl + 'products');
  }

  /**
   * Get product by ID
   */
  getProductById(id: string): Observable<Product> {
    return this.http.get<Product>(this.mainUrl + this.apiUrl + `product/${id}`);
  }

  /**
   * Get related products
   */
  getRelatedProducts(
    categoryName: string,
    id: string,
    color: string,
  ): Observable<Product[]> {
    return this.http.get<Product[]>(
      `${this.mainUrl}${this.apiUrl}products/related`,
      {
        params: {
          categoryName,
          color,
          excludeId: id,
        },
      },
    );
  }

  /**
   * Get products by category
   */
  getProductsByCategory(categoryName: string) {
    const apiUrl = this.getApiUrl();
    return this.http.get<Product[]>(
      `${this.mainUrl + apiUrl}products/by-category`,
      {
        params: { categoryName },
      },
    );
  }

  /**
   * Get products by multiple categories
   */
  getProductsByCategories(categories: string[]): Observable<Product[]> {
    const apiUrl = this.getApiUrl();
    const params = new HttpParams({ fromObject: { category: categories } });

    return this.http.get<Product[]>(
      `${this.mainUrl + apiUrl}products/by-categories`,
      { params },
    );
  }

  /**
   * Get all available sizes
   */
  getAllSizes() {
    return this.http.get<any[]>(`${this.mainUrl + this.apiUrl}sizes`);
  }

  /**
   * Get all available colors
   */
  getAllColors() {
    return this.http.get<any[]>(`${this.mainUrl + this.apiUrl}colors`);
  }

  /**
   * Filter products based on criteria
   * Note: This uses GraphQL endpoint which may need separate app/web handling
   */
  filterProducts(filters: any) {
    this.appModeService.isApp() ? (filters.platform = 'MOBILE') : '';
    return this.http.post<any>(this.mainUrl + '/graphql', {
      query: `
      query FilterProducts($filter: ProductFilterInput) {
        filterProducts(filter: $filter) {
          id
          name
          price
          description
          category { name }
          productColors {
            color
            productMedia {
              mediaUrl
            }
          }
        }
      }
    `,
      variables: {
        filter: filters,
      },
    });
  }

  /**
   * Get categories
   * Calls /api/categories for web or /api/mobile/categories for app
   */
  getCategories(): Observable<any[]> {
    const apiUrl = this.getApiUrl();
    return this.http.get<any[]>(`${this.mainUrl + apiUrl}categories`);
  }
}
